import { Box, CircularProgress, DialogContent, Divider, List, Typography } from "@mui/material"
import { ArchiveOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { usePricingForms } from "../../hooks/usePricingForms"
import { useProductSearch } from "../../hooks/useProductSearch"
import type { AddedProduct, NewRequestFormValues } from "../../types"

import { AddedProductCard } from "./AddedProductCard"
import { ConfirmedHeaderSummary } from "./ConfirmedHeaderSummary"
import { NewRequestStepActions } from "./NewRequestStepActions"
import type { PricingFormValues } from "./pricing/schemas"
import { ProductResultItem } from "./ProductResultItem"
import { ProductSearchSuggestion } from "./ProductSearchSuggestion"
import { StepSectionLabel } from "./StepSectionLabel"

import { Button } from "@/components/common/Button"

interface ProductSearchStepProps {
  formValues: NewRequestFormValues
  onCancel: () => void
  onContinue: (products: AddedProduct[], pricingByProductId: Map<number, PricingFormValues>) => void
}

export const ProductSearchStep: FC<ProductSearchStepProps> = ({ formValues, onCancel, onContinue }) => {
  const {
    searchInputValue,
    setSearchInputValue,
    addedProducts,
    addedProductIds,
    visibleResults,
    isFetching,
    showSearchResults,
    canFinalize,
    handleSearch,
    handleProductSuggestionSelect,
    handleSearchClear,
    handleProductAdd,
    handleProductRemove,
    handleRequestQuoteChange,
  } = useProductSearch(formValues.products)

  const { registerMethods, unregisterMethods, setMissingListPrice, hasMissingListPrice, validateAll, getAllValues } =
    usePricingForms()

  const quoteProductIds = addedProducts.filter((p) => p.requestQuote).map((p) => p.product.productId)
  const isFinalizeEnabled = canFinalize && !hasMissingListPrice(quoteProductIds)

  const handleFinalize = async () => {
    const isValid = await validateAll(quoteProductIds)
    if (!isValid) return
    onContinue(addedProducts, getAllValues())
  }

  return (
    <>
      <DialogContent className='flex flex-col gap-6 px-8 py-6'>
        <ConfirmedHeaderSummary formValues={formValues} />

        {addedProducts.length > 0 && (
          <Box className='flex flex-col gap-4'>
            {addedProducts.map((addedProduct) => (
              <AddedProductCard
                key={addedProduct.product.productId}
                addedProduct={addedProduct}
                customer={formValues.customer}
                initialPricingValues={formValues.pricingByProductId?.get(addedProduct.product.productId)}
                onRemove={() => handleProductRemove(addedProduct.product.productId)}
                onRequestQuoteChange={(checked) => handleRequestQuoteChange(addedProduct.product.productId, checked)}
                onRegisterMethods={registerMethods}
                onUnregisterMethods={unregisterMethods}
                onMissingListPriceChange={setMissingListPrice}
              />
            ))}
          </Box>
        )}

        <StepSectionLabel icon={<ArchiveOutlined />} label='Search for product to add' />

        <Box className='flex gap-2'>
          <ProductSearchSuggestion
            searchInputValue={searchInputValue}
            onSearchInputChange={setSearchInputValue}
            onSearch={handleSearch}
            onProductSelect={handleProductSuggestionSelect}
            onClear={handleSearchClear}
            excludedProductIds={addedProductIds}
          />
          <Button
            appearance='contained'
            startIcon={<SearchOutlined />}
            onClick={() => handleSearch(searchInputValue)}
            disabled={searchInputValue.trim().length < 2}
            data-testid='sales.product-search-step.search-button'
          >
            Search
          </Button>
        </Box>

        {showSearchResults && isFetching && visibleResults.length === 0 && (
          <Box className='flex justify-center py-2'>
            <CircularProgress size={24} />
          </Box>
        )}

        {showSearchResults && !isFetching && visibleResults.length === 0 && (
          <Typography className='text-sm text-gray-500'>No products found. Try a different search term.</Typography>
        )}

        {showSearchResults && visibleResults.length > 0 && (
          <>
            <Typography className='mt-2 inline-flex align-middle text-sm leading-6'>
              <Typography component='span' className='inline-flex font-semibold text-gray-800'>
                {visibleResults.length} results
              </Typography>
              , Select one to add to the request
            </Typography>
            <Box className='max-h-80 shrink-0 overflow-y-auto rounded border border-gray-400'>
              <List disablePadding>
                {visibleResults.map((product, index) => (
                  <Box key={product.productId}>
                    {index > 0 && <Divider />}
                    <ProductResultItem product={product} selected={false} onSelect={handleProductAdd} />
                  </Box>
                ))}
              </List>
            </Box>
          </>
        )}
      </DialogContent>

      <NewRequestStepActions
        onCancel={onCancel}
        onContinue={handleFinalize}
        continueLabel='Finalize'
        continueDisabled={!isFinalizeEnabled}
        cancelTestId='sales.product-search-step.cancel-button'
        continueTestId='sales.product-search-step.finalize-button'
      />
    </>
  )
}
